import React from "react";
import clsx from "clsx";
import { Link, useHistory } from "react-router-dom";

import AssessmentContent from "../../../organisms/teacher/AssessmentContent";
import {
  PAGE_1,
  PAGE_2,
  PAGE_3,
  PAGE_4,
  PAGE_5,
  PAGE_6,
  PAGE_7,
  X_LABELS_JSX,
} from "../../../constants/assessment-content";
import {
  IAssessmentAvgScores,
  IAssessmentDistribution,
  IClass,
} from "../../../state";
import {
  assessmentPath,
  assessmentByClassPath,
  assessmentByGradePath,
  API_URL,
} from "../../../teacherStore";
import { ReactComponent as ArrowIcon } from "../../../assets/svg/caret_down.svg";
import { ReactComponent as ArrowLeftIcon } from "../../../assets/svg/arrow_left_icon.svg";
import BarChartTeacher, {
  IAssessmentScore,
} from "../../../atoms/BarChartTeacher";
import Spinner from "../../../atoms/Spinner";
import ButtonsSwitch from "../../../atoms/ButtonsSwitch";
import useDebounceCallback from "../../../hooks/useDebounceCallback";
import ModalConfirm from "../../../molecules/ModalConfirm";
import DropdownAssessment from "../../../atoms/DropdownAssessment";

import styles from "./AssessmentByClass.module.scss";

export type Props = {
  pdfToken: string | null;
  class_id: number;
  calling?: boolean;
  currentClass: IClass;
  classIDs: number[];
  avgScoresData: IAssessmentAvgScores[] | null;
  firstAvgScoresData: IAssessmentAvgScores[] | null;
  previousAvgScoresData: IAssessmentAvgScores[] | null;
  distributions: IAssessmentDistribution[];
  redirectAllDistributionPageAction: () => void;
  onDownloadPDF: () => void;
  onClearPDFToken: () => void;
};

const pages: any = { PAGE_1, PAGE_2, PAGE_3, PAGE_4, PAGE_5, PAGE_6, PAGE_7 };

const options = [
  { value: 0, label: "初回・現在" },
  { value: 1, label: "前回・現在" },
];

const AssessmentByClass: React.FC<Props> = ({
  calling,
  class_id,
  pdfToken,
  currentClass,
  classIDs,
  avgScoresData,
  firstAvgScoresData,
  previousAvgScoresData,
  distributions,
  redirectAllDistributionPageAction,
  onDownloadPDF,
  onClearPDFToken,
}) => {
  const history = useHistory();
  const [tabActive, setTabActive] = React.useState(1);
  const [selectedTerms, setSelectedTerms] = React.useState<number[]>([]);
  const [
    currentChartData,
    setCurrentChartData,
  ] = React.useState<IAssessmentScore | null>(null);
  const [pastStudentChartData, setPastStudentChartData] = React.useState<
    IAssessmentScore[]
  >([]);
  const [selectedDropDown, setSelectedDropdown] = React.useState(
    options[0].value,
  );

  const [debounced] = useDebounceCallback(() => onDownloadPDF());
  const currentIndex = React.useMemo(
    () => classIDs.findIndex(clsID => clsID === currentClass.id),
    [currentClass.id, classIDs],
  );

  const handleClickPrevClass = React.useCallback(() => {
    setSelectedTerms([]);
    history.push({
      search: `?class_id=${
        currentIndex === 0 ? classIDs.slice(-1)[0] : classIDs[currentIndex - 1]
      }`,
    });
  }, [classIDs, history, currentIndex]);

  const handleClickNextClass = React.useCallback(() => {
    setSelectedTerms([]);
    history.push({
      search: `?class_id=${
        currentIndex === classIDs.length - 1
          ? classIDs[0]
          : classIDs[currentIndex + 1]
      }`,
    });
  }, [classIDs, history, currentIndex]);

  React.useEffect(() => {
    if (avgScoresData) {
      const pastStudentData: IAssessmentScore[] = [];
      const studentScoresCurrent: number[] = avgScoresData.map(
        item => item.student_score ?? 0,
      );

      const selectedPastScores: IAssessmentAvgScores[] | null = selectedDropDown
        ? previousAvgScoresData
        : firstAvgScoresData;

      const studentScores: number[] = selectedPastScores
        ? selectedPastScores.map(item => item.student_score ?? 0)
        : [0, 0, 0, 0, 0, 0, 0];
      pastStudentData.push({
        term_no: 0,
        scores: studentScores,
      });
      setCurrentChartData({
        term_no: 1,
        scores: studentScoresCurrent,
      });
      setPastStudentChartData([...pastStudentData]);
    } else {
      setCurrentChartData({
        term_no: 0,
        scores: [0, 0, 0, 0, 0, 0, 0],
      });
      setPastStudentChartData([]);
    }
  }, [
    selectedTerms,
    avgScoresData,
    selectedDropDown,
    firstAvgScoresData,
    previousAvgScoresData,
  ]);

  const avgScore = React.useMemo(() => {
    if (!avgScoresData) {
      return null;
    }

    const { experience_no, student_score } = avgScoresData[tabActive - 1];

    return {
      experience_no,
      score: student_score,
      reasons: [],
    };
  }, [avgScoresData, tabActive]);

  const isDownloadPDFDisabled = React.useMemo(() => {
    return (
      calling ||
      (!avgScoresData && (!firstAvgScoresData || !previousAvgScoresData))
    );
  }, [avgScoresData, calling, previousAvgScoresData, firstAvgScoresData]);

  return (
    <div className={clsx(styles.wrapper)}>
      <div className={styles.topContent}>
        <Link to={`${assessmentPath}?class_id=${currentClass.id}`}>
          <ArrowIcon /> 生徒・クラス一覧へ戻る
        </Link>
        <ButtonsSwitch
          buttonsData={[
            { content: "個人", path: assessmentPath },
            {
              content: "クラス集計",
              path: `${assessmentByClassPath}?class_id=${currentClass.id}`,
            },
            {
              content: "学年集計",
              path: `${assessmentByGradePath}?grade=${currentClass.grade}`,
            },
          ]}
        />
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.contentWrapper}>
          {calling && <Spinner className={styles.loading} />}
          <div className={styles.headerTitle}>
            {classIDs.length > 1 && (
              <button
                className={styles.arrowLeft}
                onClick={handleClickPrevClass}
              >
                <ArrowLeftIcon />
              </button>
            )}
            <span>{currentClass.full_name}</span>
            {classIDs.length > 1 && (
              <button
                className={styles.arrowRight}
                onClick={handleClickNextClass}
              >
                <ArrowLeftIcon />
              </button>
            )}
          </div>
          <button
            className={clsx(styles.downloadPDF)}
            disabled={isDownloadPDFDisabled}
            onClick={debounced}
          >
            PDFダウンロード
          </button>
          <div className="position-relative">
            {avgScoresData && (
              <DropdownAssessment
                options={options}
                defaultValue={options[0]}
                className={styles.dropTerm}
                onChange={val => {
                  setSelectedDropdown(val);
                }}
              />
            )}
            <div className={styles.summary}>
              {currentChartData && (
                <BarChartTeacher
                  currentData={currentChartData}
                  pastData={pastStudentChartData}
                />
              )}
            </div>
          </div>
          <div className={styles.switch}>
            {X_LABELS_JSX.map((label, index: number) => (
              <div
                key={index}
                className={clsx(
                  styles.switch__item,
                  tabActive === pages[`PAGE_${index + 1}`].id && [
                    styles.active,
                    styles.selected,
                  ],
                  avgScoresData &&
                    !!avgScoresData[index] &&
                    !!avgScoresData[index].student_score &&
                    styles.active,
                )}
                onClick={() => setTabActive(index + 1)}
              >
                <div>
                  <p>{index + 1}</p>
                  <span>{label}</span>
                </div>
              </div>
            ))}
          </div>
          <AssessmentContent
            distribution={distributions[tabActive - 1]}
            content={pages[`PAGE_${tabActive}`]}
            assessmentExperience={avgScore}
            hasComment={false}
          />
        </div>
      </div>
      <button
        className={styles.btnAllAssessment}
        onClick={redirectAllDistributionPageAction}
      >
        全素養分布図を見る
      </button>
      {pdfToken && (
        <ModalConfirm
          onClose={onClearPDFToken}
          title="ダウンロードしますか？"
          buttons={[
            {
              title: "キャンセル",
              action: onClearPDFToken,
              type: "None",
            },
            {
              title: "ダウンロード",
              action: () => {
                window.open(
                  `${API_URL}/assessments/download_pdf?class_id=${class_id}&temporary_token=${pdfToken}`,
                  "_blank",
                );
                onClearPDFToken();
              },
              type: "Success",
            },
          ]}
        />
      )}
    </div>
  );
};

export default AssessmentByClass;
